import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useApp } from "../context/AppContext";
import Logo from "./Logo";

const API_BASE = import.meta.env.VITE_API_URL || "";

export default function Header() {
  const { user, logout, showToast, trackEvent } = useApp();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [billingStatus, setBillingStatus] = useState(null);
  const [checkingBilling, setCheckingBilling] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Fetch billing status to show premium badge or upgrade button
    fetch(`${API_BASE}/api/billing/status`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(data => setBillingStatus(data))
      .finally(() => setCheckingBilling(false));
  }, []);

  async function handleUpgrade(plan) {
    if (upgrading) return;
    trackEvent("upgrade_checkout_start", { plan, source: "header" });
    setUpgrading(true);
    setShowUpgradeModal(false);
    try {
      const res = await fetch(`${API_BASE}/api/billing/checkout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.url) {
        throw new Error(data?.error || "Failed to start checkout");
      }
      window.location = data.url;
    } catch (e) {
      showToast(e.message || "Failed to start checkout", "error");
      setUpgrading(false);
    }
  }

  return (
    <>
      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="modal-overlay" onClick={() => setShowUpgradeModal(false)}>
          <div className="modal-content upgrade-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Choose Your Plan</h2>
              <button className="modal-close" onClick={() => setShowUpgradeModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="upgrade-plan-cards">
                {/* Monthly Plan */}
                <div className="upgrade-plan-card">
                  <div className="upgrade-plan-header">
                    <h3>Monthly</h3>
                    <div className="upgrade-plan-price">
                      <span className="upgrade-price-currency">$</span>
                      <span className="upgrade-price-amount">3.99</span>
                      <span className="upgrade-price-period">/month</span>
                    </div>
                    <p className="upgrade-plan-desc">Billed monthly</p>
                  </div>
                  <ul className="upgrade-plan-features">
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      Unlimited messages
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      Code/text attachments support
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      15000 monthly credits for built-in models
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      All supported providers
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      Works with your own API keys
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      0% markup on your own API usage
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      Custom model presets
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      Usage tracking
                    </li>
                  </ul>
                  <button
                    className="btn btn-secondary btn-full"
                    onClick={() => handleUpgrade("premium_monthly")}
                    disabled={upgrading}
                  >
                    {upgrading ? "Starting..." : "Choose Monthly"}
                  </button>
                </div>

                {/* Yearly Plan */}
                <div className="upgrade-plan-card upgrade-plan-featured">
                  <div className="upgrade-featured-badge">Save 25%</div>
                  <div className="upgrade-plan-header">
                    <h3>Yearly</h3>
                    <div className="upgrade-plan-price">
                      <span className="upgrade-price-currency">$</span>
                      <span className="upgrade-price-amount">2.99</span>
                      <span className="upgrade-price-period">/month</span>
                    </div>
                    <p className="upgrade-plan-desc">Billed as $35.88/year</p>
                  </div>
                  <ul className="upgrade-plan-features">
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      Unlimited messages
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      Code/text attachments support
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      15000 monthly credits for built-in models
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      All supported providers
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      Works with your own API keys
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      0% markup on your own API usage
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      Custom model presets
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      Usage tracking
                    </li>
                    <li>
                      <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                      </svg>
                      <strong>Save $12/year</strong>
                    </li>
                  </ul>
                  <button
                    className="btn btn-primary btn-full"
                    onClick={() => handleUpgrade("premium_yearly")}
                    disabled={upgrading}
                  >
                    {upgrading ? "Starting..." : "Choose Yearly"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="header">
        <div className="header-content">
          <div className="logo">
            <Logo size={32} />
            <h1>okeybot</h1>
          </div>
          {/* Current page chip (mobile only, see CSS) */}
          <span className="current-page-chip">
            {location.pathname.includes("/dashboard/models")
              ? "Models"
              : location.pathname.includes("/dashboard/keys")
                ? "API Keys"
                : location.pathname.includes("/dashboard/usage")
                  ? "Usage"
                  : location.pathname.includes("/dashboard/settings")
                    ? "Settings"
                    : "Dashboard"}
          </span>
          <button
            className="mobile-menu-button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <svg width={24} height={24} fill="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              ) : (
                <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
              )}
            </svg>
          </button>
          <div className={`header-nav ${mobileMenuOpen ? "mobile-open" : ""}`}>
            <NavLink
              to="/dashboard/models"
              className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}
              onClick={() => setMobileMenuOpen(false)}
            >
              Models
            </NavLink>
            <NavLink
              to="/dashboard/keys"
              className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}
              onClick={() => setMobileMenuOpen(false)}
            >
              API Keys
            </NavLink>
            <NavLink
              to="/dashboard/usage"
              className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}
              onClick={() => setMobileMenuOpen(false)}
            >
              Usage
            </NavLink>
            <NavLink
              to="/dashboard/settings"
              className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}
              onClick={() => setMobileMenuOpen(false)}
            >
              Settings
            </NavLink>
            {!checkingBilling && !billingStatus?.is_premium && (
              <button
                className="btn btn-primary btn-upgrade-mobile"
                onClick={() => { setShowUpgradeModal(true); setMobileMenuOpen(false); }}
                disabled={upgrading}
                aria-label="Upgrade to Premium"
              >
                <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
                <span className="label-full">{upgrading ? "Loading..." : "Upgrade"}</span>
                <span className="label-short">{upgrading ? "..." : "Upg"}</span>
              </button>
            )}
            {!checkingBilling && billingStatus?.is_premium && (
              <div
                className="btn btn-secondary btn-upgrade-mobile"
                style={{ cursor: "default" }}
                aria-hidden="true"
              >
                <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
                <span>Premium</span>
              </div>
            )}
          </div>
          <div className="user-info">
            {/* Show Premium badge or Upgrade button */}
            {!checkingBilling && (
              billingStatus?.is_premium ? (
                <span className="badge premium-badge">
                  <svg width={12} height={12} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                  PREMIUM
                </span>
              ) : (
                <button
                  className="btn btn-primary btn-upgrade-header hide-on-mobile"
                  onClick={() => setShowUpgradeModal(true)}
                  disabled={upgrading}
                >
                  <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                  <span className="label-full">{upgrading ? "Loading..." : "Upgrade"}</span>
                  <span className="label-short">{upgrading ? "..." : "Upg"}</span>
                </button>
              )
            )}

            {user.avatar_url ? (
              <img className="avatar" src={user.avatar_url} alt={user.username} style={{ width: 32, height: 32, borderRadius: "50%" }} />
            ) : (
              <div className="avatar">{user.username[0].toUpperCase()}</div>
            )}
            <span>{user.username}</span>
            <button className="btn btn-logout" onClick={logout} title="Logout">
              <svg width={20} height={20} fill="currentColor" viewBox="0 0 24 24">
                <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
